import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import { FadeUp, Overline } from "../components/Reveal";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { MapPin, Briefcase, ArrowDown } from "@phosphor-icons/react";

const inputCls = "h-12 bg-muted/50 border-primary/10 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", linkedin: "", cover_note: "" });
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => toast.error("Could not load open roles."));
  }, []);

  const applyTo = (job) => {
    setSelected(job);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Please choose a role to apply for.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/applications", { ...form, job_id: selected.id, job_title: selected.title });
      toast.success(`Application submitted for ${selected.title}. Good luck!`);
      setForm({ name: "", email: "", linkedin: "", cover_note: "" });
      setSelected(null);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        testId="careers-page"
        overline="Careers"
        title={["Do work that", "matters clinically."]}
        subtitle="Join scientists, engineers, clinicians, and designers building the infrastructure of modern healthcare."
      />

      <section className="pb-24 lg:pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeUp><Overline>Open roles</Overline></FadeUp>
            <div className="mt-8 space-y-4" data-testid="jobs-list">
              {jobs.map((job, i) => (
                <FadeUp key={job.id} delay={i * 0.04}>
                  <div className={`rounded-2xl border p-8 transition-all duration-300 ${selected?.id === job.id ? "border-accent bg-white shadow-lg shadow-accent/10" : "border-primary/10 bg-white hover:border-accent/40"}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h2 className="font-display text-xl font-medium tracking-tight text-foreground">{job.title}</h2>
                        <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1.5"><Briefcase size={14} /> {job.team} · {job.type}</span>
                          <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {job.location}</span>
                        </div>
                      </div>
                      <Button
                        data-testid={`apply-btn-${job.id}`}
                        onClick={() => applyTo(job)}
                        className="rounded-full bg-primary px-6 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
                      >
                        Apply <ArrowDown size={14} className="ml-1" />
                      </Button>
                    </div>
                    <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{job.description}</p>
                    <ul className="mt-4 space-y-1.5">
                      {job.requirements.map((r) => (
                        <li key={r} className="text-sm text-muted-foreground flex items-start gap-2">
                          <span className="mt-2 h-1 w-1 rounded-full bg-accent shrink-0" /> {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                </FadeUp>
              ))}
              {jobs.length === 0 && <p className="text-sm text-muted-foreground">Loading open roles…</p>}
            </div>
          </div>

          <div className="lg:col-span-5" ref={formRef}>
            <FadeUp delay={0.1}>
              <form onSubmit={submit} data-testid="application-form" className="lg:sticky lg:top-28 rounded-3xl border border-primary/10 bg-white p-8 space-y-5">
                <Overline>Apply now</Overline>
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="app-role">Role *</label>
                  <select
                    id="app-role"
                    data-testid="application-role-select"
                    required
                    value={selected?.id || ""}
                    onChange={(e) => setSelected(jobs.find((j) => j.id === e.target.value) || null)}
                    className={`mt-2 w-full rounded-md border px-3 text-sm ${inputCls}`}
                  >
                    <option value="">Select a role</option>
                    {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="app-name">Full name *</label>
                  <Input id="app-name" data-testid="application-name-input" required value={form.name} onChange={set("name")} className={`mt-2 ${inputCls}`} placeholder="Your name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="app-email">Email *</label>
                  <Input id="app-email" data-testid="application-email-input" required type="email" value={form.email} onChange={set("email")} className={`mt-2 ${inputCls}`} placeholder="you@example.com" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="app-linkedin">LinkedIn / Portfolio</label>
                  <Input id="app-linkedin" data-testid="application-linkedin-input" value={form.linkedin} onChange={set("linkedin")} className={`mt-2 ${inputCls}`} placeholder="https://linkedin.com/in/…" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground" htmlFor="app-note">Why Anxya Tech?</label>
                  <Textarea id="app-note" data-testid="application-note-input" rows={4} value={form.cover_note} onChange={set("cover_note")} className="mt-2 bg-muted/50 border-primary/10 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2" placeholder="A few lines about what draws you to this role…" />
                </div>
                <Button
                  type="submit"
                  data-testid="application-submit-btn"
                  disabled={submitting}
                  className="w-full rounded-full bg-primary h-12 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
                >
                  {submitting ? "Submitting…" : selected ? `Apply for ${selected.title}` : "Submit Application"}
                </Button>
              </form>
            </FadeUp>
          </div>
        </div>
      </section>
    </>
  );
}
